<script lang="ts">
    /**
     * @file The main entry point for the application
     * @author Carter Carling
     *
     * @since 1.0.0
     */

    import "./app.css";
    import "flowbite/dist/flowbite.min.css";
    import posthog from "posthog-js";
    import Time from "./lib/Time/Time.svelte";
    import Counter from "./lib/Counter/Counter.svelte";
    import { onDestroy, onMount } from "svelte";
    import * as Sentry from "@sentry/svelte";
    import Checklist from "./lib/Checklist/Checklist.svelte";
    import SidebarDrawer from "./lib/sidebar/SidebarDrawer.svelte";
    import { developmentVersion, State } from "./lib/state.svelte";
    import { Helper } from "./lib/helpers";
    import ScrollDownIndicator from "./lib/app/ScrollDownIndicator.svelte";
    import Navbar from "./lib/navbar/Navbar.svelte";
    import type { Optional, VersionType } from "./lib/types";
    import UpdateModal from "./lib/app/UpdateModal.svelte";
    import NoSleep from "@zakj/no-sleep";
    import NoSleepModal from "./lib/app/NoSleepModal.svelte";

    const noSleep = $state(new NoSleep());

    let updateIntervalId: Optional<number> = $state(null);

    let checkNoSleepIntervalId: Optional<number> = $state(null);

    let noSleepModalIsOpen: boolean = $state(true);

    let updateModalIsOpen: boolean = $state(false);

    function closeUpdateModal() {
        updateModalIsOpen = false;
    }

    /**
     * Checks for any updates to the app
     * @returns {Promise<boolean>} true if an update is available, false otherwise
     *
     * @see VersionType
     */
    function checkForUpdates(): Promise<{ isAvailable: boolean, data: VersionType }> {
        return new Promise(async (resolve, reject) => {
            const response = (await fetch("/version.json", {
                cache: "no-cache",
            }));

            if (response.ok) {
                const data: VersionType = await response.json();
                if (data.version !== developmentVersion && data.version !== State.version.version)
                    resolve({isAvailable: true, data});
                else
                    resolve({isAvailable: false, data});
                return;
            } else {
                Sentry.captureMessage("Failed to fetch version.json. Status code: " + response.status, "error");
                reject(new Error("Failed to fetch version.json"));
            }
        });
    }

    /**
     * Checks for updates and alerts the user if an update is available
     */
    async function handelUpdate() {
        const versionData = await checkForUpdates();

        if (State.version.version === "")
            State.version = versionData.data;

        if (versionData.data.version === developmentVersion)
            return;

        if (versionData.data.version === State.version.version)
            return;

        updateModalIsOpen = (versionData).isAvailable;
    }

    function checkNoSleep() {
        State.noSleepIsEnabled = noSleep.enabled;

        //* If noSleep is enabled, change the timer to every 5 minutes
        if (State.noSleepIsEnabled && updateIntervalId) {
            clearInterval(updateIntervalId);
            //* 5 minutes, 1000ms = 1 second, 60,000ms = 1 minute, 300,000ms = 5 minutes
            const updateIntervalTime = 1000 * 60 * 5;
            updateIntervalId = window.setInterval(handelUpdate, updateIntervalTime);
        }
    }

    function enableNoSleep() {
        noSleep.enable();
        noSleepModalIsOpen = false;
        posthog.capture('nosleep_enabled');
    }

    onMount(async () => {
        Helper.loadStateFromLocalStorage();

        await handelUpdate();

        State.noSleepIsEnabled = noSleep.enabled;
        if (State.noSleepIsEnabled)
            noSleepModalIsOpen = false;

        //* 5 minutes, 1000ms = 1 second, 60,000ms = 1 minute, 300,000ms = 5 minutes
        const updateIntervalTime = 1000 * 60 * 5;
        updateIntervalId = window.setInterval(handelUpdate, updateIntervalTime);

        //* 15 seconds, 1000ms = 1 second, 15,000ms = 15 seconds
        const checkNoSleepIntervalTime = 1000 * 5;
        checkNoSleepIntervalId = window.setInterval(checkNoSleep, checkNoSleepIntervalTime);
    });

    onDestroy(() => {

        if (noSleep.enabled)
            noSleep.disable();

        if (updateIntervalId) {
            clearInterval(updateIntervalId);
            updateIntervalId = null;
        }

        if (checkNoSleepIntervalId) {
            clearInterval(checkNoSleepIntervalId);
            checkNoSleepIntervalId = null;
        }

    });

    /**
     * Saves the state to local storage and sets the theme to the saved theme
     */
    $effect(() => {
        Helper.saveStateToLocalStorage();
        Helper.applyOptions();
    });

    /**
     * Updates the percentage of the checklist items complete
     */
    $effect(() => {
        State.options.theme;
        Helper.updatePercentage();
    });

</script>


<div class="black:bg-black frappe:bg-ctp-base frappe:ctp-frappe bg-gray-100">
    <NoSleepModal bind:isOpen={noSleepModalIsOpen} close={enableNoSleep}/>
    <UpdateModal bind:isOpen={updateModalIsOpen} close={closeUpdateModal}/>
    <nav>
        <Navbar/>
    </nav>

    <main>
        <SidebarDrawer/>
        <!--mt-[var(--small-nav-height)] vmd:mt-0-->
        <div class="grid place-items-center h-svh grid-cols-1 grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 vsm:min-h-[275px]">
            <div class="w-full h-full">
                <Time/>
            </div>
            <div class="w-full h-full">
                <Counter/>
            </div>
        </div>

        <div class="w-full h-auto py-20 grid place-items-center">
            <Checklist clear={Helper.resetChecklistToDefault}
                       updatePercentage={Helper.updatePercentageEvent}/>
        </div>

        <ScrollDownIndicator/>
    </main>
</div>
